import React, { useEffect, useRef, useState } from 'react';
import { Camera, FileUp, Files, Loader2 } from 'lucide-react';
import { uploadInvoiceFiles } from './api';
import CameraCapture from '../capture/CameraCapture';

// What a supplier sends: PDF (printed or scanned), photos and screenshots (incl. iPhone HEIC), Word, and the
// spreadsheets some send. The server decides what each file really is from its bytes; this list only stops
// obviously wrong picks early. It matches the server's allow-list (backend/upload_guard.py INVOICE_ALLOW).
export const ACCEPT = [
  '.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.webp', '.heic', '.heif', '.docx', '.csv', '.xlsx', '.json', '.xml',
  'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/webp', 'image/heic', 'image/heif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/json', 'application/xml', 'text/xml',
].join(',');
const ALLOWED = /\.(pdf|jpe?g|png|tiff?|webp|hei[cf]|docx|csv|xlsx|json|xml)$/i;
// What can be a PAGE of one invoice (backend/invoice_pages.py): a PDF or a photo.
const PAGE_ALLOWED = /\.(pdf|jpe?g|png|tiff?|webp|hei[cf])$/i;
const OLD_WORD = /\.doc$/i;

export const REJECTED_MESSAGE = 'Only PDF, photo (JPG, PNG, HEIC, TIFF, WebP), Word (.docx), spreadsheet, or JSON or XML invoice files can be read';
export const OLD_WORD_MESSAGE = 'Old Word (.doc) files cannot be read: save it as .docx or PDF and upload that';
export const PAGES_ONLY_MESSAGE = 'Only PDFs and photos can be pages of one invoice';

export function splitAllowed(files) {
  const ok = [];
  const rejected = [];
  for (const f of files) (ALLOWED.test(f.name || '') ? ok : rejected).push(f);
  return { ok, rejected };
}

/** Map the bulk response back onto the files sent: created docs by filename, the rest are duplicates. */
export function uploadsFromResponse(files, body, startedAt) {
  const docs = [...(body?.documents || [])];
  return files.map((f, i) => {
    const di = docs.findIndex((d) => (d.filename || d.original_filename) === f.name);
    const doc = di >= 0 ? docs.splice(di, 1)[0] : null;
    return { key: `${startedAt}-${i}-${f.name}`, name: f.name, startedAt, documentId: doc?.id || null, duplicate: !doc };
  });
}

/** Pages of one invoice come back as one document (or one "already uploaded"): one row. */
export function uploadFromPagesResponse(files, body, startedAt) {
  const doc = (body?.documents || [])[0] || null;
  const pages = body?.page_count || files.length;
  return [{ key: `${startedAt}-pages`, name: `One invoice, ${pages} pages (${files[0]?.name || 'scan'}…)`, startedAt,
    documentId: doc?.id || null, duplicate: !doc, pages }];
}

const btn = 'inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl font-medium disabled:opacity-50 motion-organic';

export default function InvoiceUploader({ onUploads, openScan = false }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [choice, setChoice] = useState(null);     // several files picked: { ok, bad, startedAt } until asked
  const [scanning, setScanning] = useState(Boolean(openScan));   // Today's "Scan invoice pages" opens the camera
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const separateRef = useRef(null);

  useEffect(() => { if (choice) separateRef.current?.focus(); }, [choice]);

  const reset = () => {
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const upload = async (ok, bad, startedAt, asOneDocument) => {
    setChoice(null);
    setBusy(true);
    try {
      const body = await uploadInvoiceFiles(ok, { asOneDocument });
      onUploads([...(asOneDocument ? uploadFromPagesResponse(ok, body, startedAt) : uploadsFromResponse(ok, body, startedAt)), ...bad]);
    } catch (e) {
      const failed = asOneDocument
        ? [{ key: `${startedAt}-pages-e`, name: `One invoice, ${ok.length} pages (${ok[0].name}…)`, startedAt, error: e.message }]
        : ok.map((f, i) => ({ key: `${startedAt}-e${i}`, name: f.name, startedAt, error: e.message }));
      onUploads([...failed, ...bad]);
    } finally {
      setBusy(false);
      reset();
    }
  };

  const send = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const startedAt = Date.now();
    const { ok, rejected } = splitAllowed(files);
    const bad = rejected.map((f, i) => ({ key: `${startedAt}-x${i}`, name: f.name, startedAt,
      error: OLD_WORD.test(f.name || '') ? OLD_WORD_MESSAGE : REJECTED_MESSAGE }));
    if (!ok.length) { onUploads(bad); reset(); return; }
    if (ok.length > 1) { setChoice({ ok, bad, startedAt }); return; }   // several invoices, or pages of one?
    await upload(ok, bad, startedAt, false);
  };

  // Scan pages: the camera's pages, in the order the person put them, sent as one invoice. A refusal is thrown back
  // to the scanner so the pages stay there to try again.
  const sendScan = async (blobs) => {
    const startedAt = Date.now();
    const files = blobs.map((b, i) => new File([b], `scan-page-${i + 1}.jpg`, { type: b.type || 'image/jpeg' }));
    const body = await uploadInvoiceFiles(files, { asOneDocument: true });
    setScanning(false);
    onUploads(uploadFromPagesResponse(files, body, startedAt));
  };

  const pagesOk = choice && choice.ok.every((f) => PAGE_ALLOWED.test(f.name || ''));

  return (
    <section
      data-testid="invoice-uploader"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer?.files); }}
      className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 motion-organic ${drag ? 'border-primary-400 bg-primary-50' : 'border-surface-border bg-surface'}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1">
          <h2 className="font-display text-xl text-ink">Add supplier invoices</h2>
          <p className="text-sm text-ink-muted mt-1">
            Drop files here, or choose them: a PDF, a photo or screenshot (JPG, PNG, HEIC, TIFF, WebP), a Word file, or an invoice file a supplier sends (CSV, JSON or XML). You can add several at once. An invoice on more than one page? Use Scan pages, or choose all its pages together.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <button type="button" data-testid="invoice-scan-pages-btn" disabled={busy}
            onClick={() => setScanning(true)}
            className={`${btn} bg-accent-500 text-white hover:bg-accent-600`}>
            <Files className="w-5 h-5" /> Scan pages
          </button>
          <button type="button" data-testid="invoice-take-photo-btn" disabled={busy}
            onClick={() => cameraRef.current?.click()}
            className={`${btn} border border-accent-500 text-accent-600 bg-surface hover:bg-accent-50`}>
            <Camera className="w-5 h-5" /> Take photo
          </button>
          <button type="button" data-testid="invoice-choose-files-btn" disabled={busy}
            onClick={() => fileRef.current?.click()}
            className={`${btn} bg-primary-500 text-surface hover:bg-primary-600`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileUp className="w-5 h-5" />}
            {busy ? 'Uploading…' : 'Choose files'}
          </button>
        </div>
      </div>

      {choice && (
        <div role="group" aria-labelledby="invoice-multi-choice-q" data-testid="invoice-multi-choice"
          className="mt-5 rounded-xl border border-surface-border bg-surface-muted p-4 space-y-3">
          <p id="invoice-multi-choice-q" className="text-sm text-ink">
            You chose {choice.ok.length} files. Are they separate invoices, or the pages of one invoice (in the order shown)?
          </p>
          <ol className="text-xs text-ink-muted list-decimal pl-5">
            {choice.ok.map((f, i) => <li key={`${f.name}-${i}`}>{f.name}</li>)}
          </ol>
          <div className="flex flex-col sm:flex-row gap-3">
            <button ref={separateRef} type="button" data-testid="invoice-choice-separate"
              onClick={() => upload(choice.ok, choice.bad, choice.startedAt, false)}
              className={`${btn} bg-primary-500 text-surface hover:bg-primary-600`}>
              Separate invoices
            </button>
            <button type="button" data-testid="invoice-choice-one" disabled={!pagesOk}
              onClick={() => upload(choice.ok, choice.bad, choice.startedAt, true)}
              className={`${btn} border border-primary-500 text-primary-600 bg-surface hover:bg-primary-50`}>
              Pages of one invoice
            </button>
            <button type="button" data-testid="invoice-choice-cancel" onClick={() => { setChoice(null); reset(); }}
              className={`${btn} text-ink-muted hover:bg-surface-sunken`}>
              Cancel
            </button>
          </div>
          {!pagesOk && <p className="text-xs text-ink-muted" data-testid="invoice-choice-hint">{PAGES_ONLY_MESSAGE}; send the other files as separate invoices.</p>}
        </div>
      )}

      {scanning && (
        <div className="mt-5 space-y-3" data-testid="invoice-scan-pages">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-ink">
              Take page 1, then page 2 and so on. Put them in order, retake any that are blurred, then send them as one invoice.
            </p>
            <button type="button" data-testid="invoice-scan-cancel" onClick={() => setScanning(false)}
              className="min-h-[44px] px-4 rounded-lg text-sm text-ink-muted hover:bg-surface-sunken">
              Cancel
            </button>
          </div>
          <CameraCapture captureType="supplier_invoice" onSendPages={sendScan} />
        </div>
      )}

      <input ref={fileRef} type="file" multiple accept={ACCEPT} className="sr-only" tabIndex={-1}
        aria-label="Choose invoice files" data-testid="invoice-file-input" onChange={(e) => send(e.target.files)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1}
        aria-label="Take a photo of an invoice" data-testid="invoice-camera-input" onChange={(e) => send(e.target.files)} />
    </section>
  );
}
